import {PIXI} from '@colonist/pixi'
import {UIGameManager} from '../../ui_game/UIGameManager'
import {TradeViewSpriteButton} from '../TradeViewSpriteButton'
import {UIGameBaseTradeOfferActionsView} from './UIGameBaseTradeOfferActionsView'
import {TranslationObject} from '@colonist/i18n'
import {FloatingText} from '../../game_ui_view_objects/FloatingText'
import {Sprites} from '../../ui_game/UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {TradeOfferOpponentDisplay, TradePlayerCreatingCounterOfferStatus, TradePlayerResponseStatus, TradeValidator} from '@colonist/game-mechanic-validators'
import {PlayerColor, TradeId, TradeResponseStatus} from '@colonist/game-states'

export class UIGameTradeOfferActionsViewMyTurn extends UIGameBaseTradeOfferActionsView {
    private readonly playerResponseButtons: Map<PlayerColor, TradeViewSpriteButton>
    private readonly counterOfferButton?: TradeViewSpriteButton | undefined
    readonly rejectTradeButton?: TradeViewSpriteButton | undefined

    constructor(uiGameManager: UIGameManager, container: PIXI.Container, x: number, y: number, actionSize: number, stateController: TradeValidator) {
        super(uiGameManager, container, x, y, actionSize, stateController)

        this.playerResponseButtons = new Map()

        if(this.stateController.getOfferComponentsToDisplay(this.uiGameManager.gameController.myColor) == TradeOfferOpponentDisplay.ResponseButtons) {
            this.stateController.getPlayerResponseStates().forEach((playerResponse) => {
                const button = this.createPlayerResponseButton(this.stateController.state.id, playerResponse)
                this.playerResponseButtons.set(playerResponse.player, button)
            })
        }

        this.counterOfferButton = this.createCounterOfferButton()
        this.rejectTradeButton = this.createRejectOfferButton()

        const timeToAutoReject = this.stateController.getTradeAutoRejectionTime(this.uiGameManager.gameController.gameSettings.gameSpeed, this.uiGameManager.gameController.myColor)
        if(!this.amITradeCreator()) this.triggerAutoRejectionCountdown(timeToAutoReject)
    }

    onPlayerResponseStatus() {
        for(const playerResponse of this.stateController.getPlayerResponseStates()) {
            const existingButton = this.playerResponseButtons.get(playerResponse.player)
            if(existingButton === undefined) return

            const oldButtonX = existingButton.x
            existingButton.destroy()

            const newButton = this.createPlayerResponseButton(this.stateController.state.id, playerResponse)
            newButton.x = oldButtonX

            this.playerResponseButtons.set(playerResponse.player, newButton)
            this.restartAllCreatingCounterOfferIcons()
        }
    }

    onPlayerCreatingCounterOffer(tradePlayerCreatingCounterOffer: TradePlayerCreatingCounterOfferStatus) {
        const button = this.playerResponseButtons.get(tradePlayerCreatingCounterOffer.player)
        if(button == undefined) return

        const playerName = this.uiGameManager.gameController.getPlayerState(tradePlayerCreatingCounterOffer.player).username
        tradePlayerCreatingCounterOffer.isCreatingCounterOffer ? button.showCreatingCounterOfferWaitIcon(playerName) : button.hideCreatingCounterOfferWaitIcon()

        this.restartAllCreatingCounterOfferIcons()
    }

    private restartAllCreatingCounterOfferIcons() {
        for(const playerResponse of this.stateController.getPlayersCreatingCounterOffer()) {
            const button = this.playerResponseButtons.get(playerResponse.player)
            if(button == undefined) continue
            button.restartCreatingCounterOfferWaitIcon()
        }
    }

    private createPlayerResponseButton(tradeId: TradeId, playerResponse: TradePlayerResponseStatus): TradeViewSpriteButton {
        const executeTradeAction = this.stateController.getExecuteTradeActionStatus(this.uiGameManager.gameController.myColor)

        const isEnabled = executeTradeAction.canTakeAction ? playerResponse.responseStatus === TradeResponseStatus.Accepted : false
        const texture = UIGameTradeOfferActionsViewMyTurn.getPlayerResponseButtonTexture(playerResponse.responseStatus)
        const bgColor = playerResponse.player
        const button = this.createButton(isEnabled, texture, bgColor)

        const action = () => {
            if(!executeTradeAction.canTakeAction) {
                if(executeTradeAction.errorMessageIfCantTakeAction == undefined) return
                new FloatingText(executeTradeAction.errorMessageIfCantTakeAction, button)
                return
            }
            const hasPlayerAcceptedTrade = playerResponse.responseStatus == TradeResponseStatus.Accepted
            if(!hasPlayerAcceptedTrade) {
                const floatingTextError = this.getTextTipForStatus(playerResponse)
                if(floatingTextError == undefined) return
                new FloatingText(floatingTextError, button)
                return
            }
            this.uiGameManager.socketGameSend.executeTrade(tradeId, playerResponse.player)
        }
        button.updateAction(action)

        const toolTip = this.getTextTipForStatus(playerResponse)
        button.setTip({key: 'strings:colonistCommon.utils.emptyString'}, [toolTip])

        const isPlayerEmbargoed = playerResponse.responseStatus == TradeResponseStatus.Embargoed
        if(isPlayerEmbargoed) button.activateEmbargoState()

        const playerCreatingCounterOfferStatus = this.stateController.getPlayersCreatingCounterOffer().find((playerCreatingCounterOffer) => playerCreatingCounterOffer.player == playerResponse.player)
        if(playerCreatingCounterOfferStatus?.isCreatingCounterOffer) {
            const playerName = this.uiGameManager.gameController.getPlayerState(playerResponse.player).username
            button.showCreatingCounterOfferWaitIcon(playerName)
        }

        return button
    }

    private static getPlayerResponseButtonTexture(responseStatus: TradeResponseStatus): PIXI.Texture {
        switch(responseStatus) {
            case TradeResponseStatus.Accepted: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_check)
            case TradeResponseStatus.PendingResponse: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_hourglass)
            case TradeResponseStatus.Rejected: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_x)
            case TradeResponseStatus.Embargoed: return UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_trade)
        }
    }

    private getTextTipForStatus(playerResponse: TradePlayerResponseStatus): TranslationObject {
        const playerName = this.uiGameManager.gameController.getPlayerState(playerResponse.player).username
        switch(playerResponse.responseStatus) {
            case TradeResponseStatus.Accepted: return {
                key: 'strings:game.tooltips.tradeOfferExecuteTrade',
                options: {
                    playerUsername: playerName,
                },
            }
            case TradeResponseStatus.Rejected: return {
                key: 'strings:game.tooltips.tradeOfferPlayerRejected',
                options: {
                    playerUsername: playerName,
                },
            }
            case TradeResponseStatus.PendingResponse: return {
                key: 'strings:game.tooltips.tradeOfferPlayerPending',
                options: {
                    playerUsername: playerName,
                },
            }
            case TradeResponseStatus.Embargoed: return {
                key: 'strings:game.tooltips.tradeOfferPlayerEmbargoed',
                options: {
                    playerUsername: playerName,
                    context: 'self',
                }}
        }
    }
}
